import {
  Group,
  Text,
  Tooltip,
  useMantineTheme,
  ActionIcon,
} from "@mantine/core"
import {
  IconDotsVertical,
  IconEye,
  IconEyeOff,
  IconPin,
  IconPinnedOff,
  IconColumns2,
} from "@tabler/icons-react"
import { Header, flexRender } from "@tanstack/react-table"
import { useState } from "react"

import {
  useTextOverflow,
  Menu,
  CHECKBOX_COLUMN_ID,
  TableTranslationsKeys,
} from "@tfg/ui"

import { SortIcon } from "../../atoms/SortIcon/SortIcon"

interface TableHeaderCellProps<T> {
  header: Header<T, unknown>
  setOpenColumnManager?: React.Dispatch<React.SetStateAction<boolean>>
  translations: Record<keyof typeof TableTranslationsKeys, string>
}

export const TableHeaderCell = <T,>({
  header,
  setOpenColumnManager,
  translations,
}: TableHeaderCellProps<T>) => {
  const { textRef, isOverflowing } = useTextOverflow()
  const theme = useMantineTheme()
  const headerContent = flexRender(
    header.column.columnDef.header,
    header.getContext()
  )
  const [isOpen, setIsOpen] = useState(false)
  const [isHovered, setIsHovered] = useState(false)
  const isCheckboxColumn = header.column.id === CHECKBOX_COLUMN_ID

  return (
    <Menu
      opened={isOpen}
      onClose={() => setIsOpen(false)}
      closeOnClickOutside
      closeOnEscape
    >
      <Group
        wrap="nowrap"
        justify={isCheckboxColumn ? "center" : "space-between"}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        style={{ width: "100%" }}
      >
        <Group
          wrap="nowrap"
          justify={isCheckboxColumn ? "center" : "flex-start"}
          style={{ flex: 1, minWidth: 0 }}
        >
          {isCheckboxColumn ? (
            <Group w={theme.other.table.cells.checkbox}>{headerContent}</Group>
          ) : isOverflowing && typeof headerContent === "string" ? (
            <Tooltip label={headerContent}>
              <Text
                fw="var(--table-header-text-weight)"
                fz="var(--table-header-text-size)"
                ref={textRef}
                truncate
              >
                {headerContent}
              </Text>
            </Tooltip>
          ) : typeof headerContent !== "string" ? (
            headerContent
          ) : (
            <Text fw={600} ref={textRef} truncate>
              {headerContent}
            </Text>
          )}
          {header.column.getCanSort() && (
            <Group>
              <SortIcon sortOrder={header.column.getIsSorted()} />
            </Group>
          )}
        </Group>

        {!isCheckboxColumn && !header.column.id.startsWith("action-cell") && (
          <Menu.Target>
            <ActionIcon
              size={18}
              variant="ghost"
              color="var(--mantine-color-dimmed)"
              onClick={(e) => {
                e.stopPropagation()
                setIsOpen(true)
              }}
              style={{
                opacity: isHovered || isOpen ? 1 : 0,
                transition: "opacity 150ms ease",
                flexShrink: 0,
              }}
            >
              <IconDotsVertical
                size={theme.other.icon.size}
                stroke={theme.other.icon.stroke}
              />
            </ActionIcon>
          </Menu.Target>
        )}
      </Group>
      <Menu.Dropdown onMouseLeave={() => setIsOpen(false)}>
        {header.column.getCanPin() && (
          <>
            {!header.column.getIsPinned() ? (
              <>
                <Menu.Item
                  onClick={() => {
                    header.column.pin("left")
                    setIsOpen(false)
                  }}
                  fz="sm"
                >
                  <Group
                    fz="sm"
                    justify="space-between"
                    w="100%"
                    miw="max-content"
                    style={{ cursor: "pointer" }}
                  >
                    <Text>
                      {translations.pinColumn ??
                        TableTranslationsKeys.pinColumn}
                    </Text>
                    <IconPin
                      size={theme.other.icon.size}
                      stroke={theme.other.icon.stroke}
                    />
                  </Group>
                </Menu.Item>
              </>
            ) : (
              <Menu.Item fz="sm">
                <Group
                  w="100%"
                  miw="max-content"
                  justify="space-between"
                  onClick={() => {
                    header.column.pin(false)
                    setIsOpen(false)
                  }}
                  style={{ cursor: "pointer" }}
                >
                  <Text>
                    {translations.unpinColumn ??
                      TableTranslationsKeys.unpinColumn}
                  </Text>
                  <IconPinnedOff
                    size={theme.other.icon.size}
                    stroke={theme.other.icon.stroke}
                  />
                </Group>
              </Menu.Item>
            )}
          </>
        )}

        {header.column.getCanHide() && (
          <>
            {!header.column.getIsVisible() ? (
              <Menu.Item
                onClick={() => {
                  header.column.toggleVisibility(true)
                  setIsOpen(false)
                }}
                disabled={header.column.getIsPinned() === "left"}
              >
                <Group wrap="nowrap" justify="space-between">
                  <Text>
                    {translations.showColumn ??
                      TableTranslationsKeys.showColumn}
                  </Text>
                  <IconEye
                    size={theme.other.icon.size}
                    stroke={theme.other.icon.stroke}
                  />
                </Group>
              </Menu.Item>
            ) : (
              <Menu.Item
                onClick={() => {
                  header.column.toggleVisibility(false)
                  setIsOpen(false)
                }}
                disabled={header.column.getIsPinned() === "left"}
              >
                <Group wrap="nowrap" justify="space-between">
                  <Text>
                    {translations.hideColumn ??
                      TableTranslationsKeys.hideColumn}
                  </Text>
                  <IconEyeOff
                    size={theme.other.icon.size}
                    stroke={theme.other.icon.stroke}
                  />
                </Group>
              </Menu.Item>
            )}
          </>
        )}
        {setOpenColumnManager && (
          <Menu.Item onClick={() => setOpenColumnManager?.(true)}>
            <Group wrap="nowrap" justify="space-between">
              <Text>
                {translations.manageColumns ??
                  TableTranslationsKeys.manageColumns}
              </Text>
              <IconColumns2
                size={theme.other.icon.size}
                stroke={theme.other.icon.stroke}
              />
            </Group>
          </Menu.Item>
        )}
      </Menu.Dropdown>
    </Menu>
  )
}
